/*
 * TICKET-01430: crash/wipe protection for not-yet-created journals.
 *
 * A new journal ("Ny journal") only exists in the shared journalStore.newJournal
 * observable until the vet presses "Opprett journal" — per-field autosave needs an id
 * (input.js keeps id-less edits memory-only), so a drawer remount, reload or tab crash
 * silently destroys everything typed. A vet lost 40 minutes of journal text this way.
 *
 * This module mirrors id-less drafts to localStorage so stores/journal.js can restore
 * them when a new journal for the same sale+animal is opened. Storage is a single JSON
 * MAP keyed by "sale:animal": a rescued draft for animal A must never be overwritten or
 * cleared by typing/stamping on animal B (review finding on the single-slot design).
 * Pure wrapper — no Preact / no store deps — and fail-safe: any storage error
 * (disabled, full, private mode) degrades to a no-op so journal writing never breaks.
 * `storage` and `now` are injectable for tests.
 */

import { STAMPED_OR_SYSTEM_FIELDS } from './journalSaveDiff';

const KEY = 'keepspot.journalDraft';

// Drafts older than this are stale enough that restoring them would surprise more
// than it would help.
export const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000;

// Backstop against unbounded growth if a clinic never revisits its drafts.
const MAX_DRAFTS = 50;

// endDate is stamped by a focusCallback (+30 min) without the vet typing anything,
// so it is never draft content and must not overwrite a freshly computed value.
const DRAFT_EXCLUDED_FIELDS = new Set([...STAMPED_OR_SYSTEM_FIELDS, 'endDate']);

// Arrays that id-less journals accumulate purely client-side until the journal is
// created: addDiagnose.js and addMedicine.js both have "new journal without ID —
// update locally only" branches (review P1). All other arrays (prescriptions,
// images, files, ...) require an existing journal id and are never mirrored.
const DRAFT_ARRAY_FIELDS = new Set(['diagnoses', 'medicine']);

function resolveStorage(storage) {
    if (storage) {
        return storage;
    }
    try {
        return typeof window !== 'undefined' ? window.localStorage : null;
    } catch (e) {
        return null;
    }
}

const isMeaningfulPrimitive = value => {
    if (typeof value === 'string') {
        return value.trim() !== '';
    }
    if (typeof value === 'number') {
        return Number.isFinite(value) && value !== 0;
    }
    if (typeof value === 'boolean') {
        return value;
    }
    return false;
};

// The persistable user content of a journal: meaningful primitives outside the
// stamped/system set, plus the whitelisted client-side arrays when non-empty.
function pickDraftFields(journal) {
    const fields = {};
    Object.keys(journal || {}).forEach(key => {
        if (DRAFT_EXCLUDED_FIELDS.has(key)) {
            return;
        }
        if (/(Norm|SearchTokens)$/.test(key)) {
            return;
        }
        const value = journal[key];
        if (DRAFT_ARRAY_FIELDS.has(key)) {
            if (Array.isArray(value) && value.length > 0) {
                fields[key] = value;
            }
            return;
        }
        if (isMeaningfulPrimitive(value)) {
            fields[key] = value;
        }
    });
    return fields;
}

const pairKey = ({ sale, animal } = {}) => (sale > 0 && animal > 0 ? `${sale}:${animal}` : null);

function readMap(s) {
    try {
        const raw = s.getItem(KEY);
        if (!raw) {
            return {};
        }
        const map = JSON.parse(raw);
        return (map && typeof map === 'object' && !Array.isArray(map)) ? map : {};
    } catch (e) {
        try {
            s.removeItem(KEY);
        } catch (e2) {
            // ignore
        }
        return {};
    }
}

function writeMap(s, map) {
    try {
        if (Object.keys(map).length === 0) {
            s.removeItem(KEY);
        } else {
            s.setItem(KEY, JSON.stringify(map));
        }
    } catch (e) {
        // quota / disabled — ignore
    }
}

// Drop expired entries; if still over the cap, drop the oldest.
function prune(map, now) {
    const keys = Object.keys(map);
    keys.forEach(key => {
        const entry = map[key];
        if (!entry || !entry.savedAt || !entry.fields || (now - entry.savedAt) > DRAFT_TTL_MS) {
            delete map[key];
        }
    });
    const remaining = Object.keys(map);
    if (remaining.length > MAX_DRAFTS) {
        remaining
            .sort((a, b) => (map[a].savedAt || 0) - (map[b].savedAt || 0))
            .slice(0, remaining.length - MAX_DRAFTS)
            .forEach(key => { delete map[key]; });
    }
    return map;
}

// The stored draft for this sale+animal, or null. Shape:
// { savedAt, sale, animal, visitor, fields }.
export function loadDraft({ sale, animal } = {}, storage, now = Date.now()) {
    const s = resolveStorage(storage);
    const key = pairKey({ sale, animal });
    if (!s || !key) {
        return null;
    }
    const entry = readMap(s)[key];
    if (!entry || !entry.fields || !entry.savedAt || (now - entry.savedAt) > DRAFT_TTL_MS) {
        return null;
    }
    return { sale, animal, ...entry };
}

// Remove the draft for exactly this sale+animal (e.g. after that journal was
// successfully created). Other pairs' drafts are untouched.
export function clearDraft({ sale, animal } = {}, storage) {
    const s = resolveStorage(storage);
    const key = pairKey({ sale, animal });
    if (!s || !key) {
        return;
    }
    const map = readMap(s);
    if (key in map) {
        delete map[key];
        writeMap(s, map);
    }
}

// Mirror an id-less journal into its pair's slot. Content-bearing → write; emptied by
// the vet → clear its own slot (so deliberately deleted text is not resurrected).
// Journals with an id are ignored — per-field autosave owns those. Journals without
// sale+animal are ignored — there is nothing to restore them against.
export function saveDraft(journal, storage, now = Date.now()) {
    const s = resolveStorage(storage);
    const key = journal && !journal.id ? pairKey(journal) : null;
    if (!s || !key) {
        return;
    }
    const map = prune(readMap(s), now);
    const fields = pickDraftFields(journal);
    if (Object.keys(fields).length === 0) {
        delete map[key];
    } else {
        map[key] = {
            savedAt: now,
            visitor: journal.visitor || 0,
            fields,
        };
    }
    writeMap(s, map);
}
